/**
 * Matriz Impacto × Urgencia → Prioridad (estilo ServiceNow / ITIL).
 *
 * Impacto y Urgencia se expresan con 1 = Alto/Alta, 2 = Medio/Media, 3 = Bajo/Baja
 * (igual que ServiceNow, donde 1 es el nivel más severo). La prioridad resultante
 * usa los ids del catálogo `Tickets.priority`: 1 Baja · 2 Media · 3 Alta · 4 Crítica.
 */

/**
 * Niveles de Impacto (1 = más severo).
 * El impacto responde a «¿a cuánta gente o a qué tan crítico es lo afectado?».
 */
export const IMPACT_LEVELS = [
  {
    value: 1,
    label: 'Alto',
    color: '#dc2626',
    hint: 'Afecta a muchos usuarios o un servicio crítico',
    examples: [
      'Toda la oficina sin acceso al sistema',
      'Se detiene la facturación o la operación',
    ],
  },
  {
    value: 2,
    label: 'Medio',
    color: '#d97706',
    hint: 'Afecta a un grupo o servicio secundario',
    examples: [
      'Un departamento no puede usar un módulo',
      'Hay forma de seguir trabajando, pero incómoda',
    ],
  },
  {
    value: 3,
    label: 'Bajo',
    color: '#16a34a',
    hint: 'Afecta a una persona o es menor',
    examples: [
      'Sólo a usted, y puede continuar su trabajo',
      'Un detalle visual o una mejora puntual',
    ],
  },
]

/**
 * Niveles de Urgencia (1 = más urgente).
 * La urgencia responde a «¿qué tan rápido se necesita?», independientemente
 * de a cuánta gente afecte.
 */
export const URGENCY_LEVELS = [
  {
    value: 1,
    label: 'Alta',
    color: '#dc2626',
    hint: 'Requiere atención inmediata',
    examples: [
      'Bloquea un cierre, una entrega o un pago de hoy',
      'No hay ninguna forma de continuar',
    ],
  },
  {
    value: 2,
    label: 'Media',
    color: '#d97706',
    hint: 'Puede esperar un tiempo razonable',
    examples: [
      'Se necesita esta semana',
      'Existe una solución temporal mientras tanto',
    ],
  },
  {
    value: 3,
    label: 'Baja',
    color: '#16a34a',
    hint: 'No es sensible al tiempo',
    examples: [
      'Puede programarse cuando haya espacio',
      'No afecta ninguna fecha comprometida',
    ],
  },
]

/**
 * Matriz de derivación: PRIORITY_MATRIX[impacto][urgencia] = priorityId.
 * (1 Baja · 2 Media · 3 Alta · 4 Crítica)
 */
export const PRIORITY_MATRIX = {
  1: { 1: 4, 2: 3, 3: 2 }, // Impacto Alto
  2: { 1: 3, 2: 2, 3: 1 }, // Impacto Medio
  3: { 1: 2, 2: 1, 3: 1 }, // Impacto Bajo
}

/** Metadatos visuales de cada prioridad (fallback de etiqueta y color). */
export const PRIORITY_META = {
  1: { label: 'Baja', color: '#16a34a' },
  2: { label: 'Media', color: '#d97706' },
  3: { label: 'Alta', color: '#dc2626' },
  4: { label: 'Crítica', color: '#9d174d' },
}

/**
 * Deriva la prioridad (priorityId) a partir del impacto y la urgencia.
 * @param {number} impact - 1..3
 * @param {number} urgency - 1..3
 * @returns {number} priorityId (1..4); por defecto 2 (Media) si los valores son inválidos.
 */
export const derivePriorityId = (impact, urgency) =>
  PRIORITY_MATRIX[Number(impact)]?.[Number(urgency)] ?? 2

/**
 * Metadatos de una prioridad por id.
 * @param {number} id
 * @returns {{label: string, color: string}}
 */
export const getPriorityMeta = (id) => PRIORITY_META[id] || PRIORITY_META[2]

/**
 * Metadatos de un nivel de impacto.
 * @param {number} value - 1..3
 * @returns {{value: number, label: string, color: string, hint: string, examples: string[]}}
 */
export const getImpactLevel = (value) =>
  IMPACT_LEVELS.find((lvl) => lvl.value === Number(value)) || IMPACT_LEVELS[1]

/**
 * Metadatos de un nivel de urgencia.
 * @param {number} value - 1..3
 * @returns {{value: number, label: string, color: string, hint: string, examples: string[]}}
 */
export const getUrgencyLevel = (value) =>
  URGENCY_LEVELS.find((lvl) => lvl.value === Number(value)) || URGENCY_LEVELS[1]
